import type { IAccount } from "@/modules/account/common/types";
import { CARD_NUMBER_LENGTH, CURRENCY } from "@/shared/common/constants";
import { substringAmount } from "@/shared/helpers";

interface IFormatCardNumberProps {
  card: string;
  isHidden?: boolean;
}

export const formatCardNumber = ({ card, isHidden }: IFormatCardNumberProps): string => {
  const clean = card?.replace(/ /g, "").replace(/\D/g, "");
  const sliced = clean?.length > CARD_NUMBER_LENGTH ? clean.slice(0, CARD_NUMBER_LENGTH) : clean;
  if (sliced) {
    if (isHidden) {
      return sliced?.replace(/^(\d{4})\d{4}\d{4}(\d{4})$/, "$1 **** **** $2");
    }
    return sliced?.replace(/(.{4})/g, "$1 ").trim();
  }
  return "";
};

export const renderSelectedAccount = (account: IAccount) => {
  return `${account.number} - ${substringAmount(account.expectedBalance)} ${CURRENCY[account.currency]?.symbol}`;
};

export const isValidCard = (value: string) => /^\d{4}( ?\d{4}){3}$/.test(value);
export const isValidAccount = (value: string) => /^[0-9]{12,16}$/.test(value);
export const isAllowedAccountNumberTyping = (value: string) => /^[0-9]{0,16}$/.test(value);
export const isValidAccountChecksum = (account?: string): boolean => {
  const last_index = 11;
  if (!account || account.length <= last_index) return false;

  const baseDigits = account
    .split("")
    .map(Number);

  let sum = 0;

  for (let i = 0;i < last_index;i++) {
    const digit = baseDigits[i];
    const multiplicationNumber = i % 2 === 0 ? 2 : 1;
    const val = digit * multiplicationNumber;

    sum += val > 9 ? Math.floor(val / 10) + (val % 10) : val;
  }

  return (sum + baseDigits[last_index]) % 10 === 0;
};

export const getCardType = (number: string) => {
  const num = number.replace(/\D/g, "");

  const patterns = {
    Visa: /^4\d{12}(\d{3})?(\d{3})?$/,
    Mastercard: /^(5[1-5]\d{4}|677189)\d{10}$|^2(2[2-9]\d{2}|[3-6]\d{3}|7[01]\d{2}|720\d)\d{10}$/,
    Amex: /^3[47]\d{13}$/,
    Discover: /^6(?:011|5\d{2}|4[4-9]\d)\d{12,15}$/,
    DinersClub: /^3(?:0[0-5]|[68]\d)\d{11}$/,
    JCB: /^35(?:2[89]|[3-8]\d)\d{12}$/,
    UnionPay: /^62\d{14,17}$/,
    Arca: /^9051\d{12}$/,
  };

  for (const [type, regex] of Object.entries(patterns)) {
    if (regex.test(num)) return type;
  }
  return "Unknown";
};
